import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { DialogOverlay } from '@reach/dialog';
import { gql, useQuery } from '@apollo/client';
import { ReactComponent as IconClose } from '../assets/icons/close-modal.svg';
import { Heading } from './Type';
import '@reach/dialog/styles.css';
import Button from './Button';
import { ReactComponent as IconRightArrow } from '../assets/icons/right-arrow.svg';

interface OnlineStockistModalProps {
    show: boolean;
    close: () => void;
}

const StyledClose = styled.button`
    border: 0;
    padding: 0;
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(50%, -50%);
    background-color: transparent;
    svg {
        width: 32px;
        height: 32px;
    }
`;
const StyledHeading = styled.div`
    ${({ theme }) => css`
        background: ${theme.colors.brand};
        width: 100%;
        color: #fff;
        padding: 24px;
        text-align: center;
    `}
`;

const StyledDialogContent = styled.div`
    ${({ theme }) => css`
        box-shadow: 0px 10px 50px hsla(0, 0%, 0%, 0.33);
        position: relative;
        padding: 0;
        width: 90vw;
        margin: 10vh auto;
        background: white;
        outline: none;
        z-index: 110;
        max-width: 630px;

        @media (min-width: ${theme.breakpoints.lg}px) {
            width: 50vw;
        }
    `}
`;

const StyledLogo = styled.div`
    ${({ theme }) => css`
        flex-direction: column;

        justify-content: center;
        img {
            width: 100%;
            max-width: 110px;
            height: auto;
            flex-shrink: 0;
            @media (min-width: ${theme.breakpoints.sm}px) {
                max-width: 220px;
            }
        }
    `}
`;
const StyledItems = styled.div`
    > * + * {
        border-top: 1px solid #eeeeee;
    }
`;
const StyledItem = styled.div`
    ${({ theme }) => css`
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: space-between;
        padding: ${theme.space.sm};
        align-items: center;

        @media (min-width: ${theme.breakpoints.sm}px) {
            padding: ${theme.space.lg};
        }

        .stock-button {
            max-height: 48px;
        }
    `}
`;

const StyledItemAction = styled(StyledItem)`
    flex-direction: column;
`;

const GET_ONLINE = gql`
    query OnlineStockists {
        onlineStockists(where: { orderby: { field: MENU_ORDER, order: ASC }, isFeatured: true }, first: 4) {
            nodes {
                title
                id
                featuredImage {
                    node {
                        sourceUrl(size: LOGO)
                        altText
                    }
                }
                details: onlineStockistDetails {
                    website
                }
            }
        }
    }
`;
const OnlineStockistModal: FC<OnlineStockistModalProps> = ({ close, show }) => {
    const { loading, error, data } = useQuery(GET_ONLINE);

    const handleClick = (url) => {
        window?.dataLayer?.push({
            event: 'gtmClick',
            element: {
                category: 'find a stockist',
                name: 'online stockist cta', // set as constant even if the element text changes
                text: 'shop now', // click text of the element clicked (if applicable)
                detail: url, // domain of the online stockist
            },
        });

        window.open(url, '_blank');
    };
    const handleAll = () => {
        window?.dataLayer?.push({
            event: 'gtmClick',
            element: {
                category: 'find a stockist',
                name: 'stockists modal - show all online stockists', // set as constant even if the element text changes
                text: 'Show all online stockists', // click text of the element clicked
            },
        });
        window.location.href = '/online-stockists';
    };
    return (
        <>
            <DialogOverlay isOpen={show} onDismiss={close}>
                <StyledDialogContent>
                    <StyledHeading>
                        <Heading noMargin>Online Stockists</Heading>
                    </StyledHeading>
                    <StyledItems>
                        {!error &&
                            !loading &&
                            data?.onlineStockists?.nodes.map(({ title, featuredImage, details, id }) => (
                                <StyledItem key={id}>
                                    {featuredImage?.node?.sourceUrl && (
                                        <StyledLogo>
                                            <img
                                                src={featuredImage.node.sourceUrl}
                                                alt={featuredImage.altText || title}
                                            />
                                        </StyledLogo>
                                    )}

                                    <Button
                                        className="stock-button"
                                        size="large"
                                        label="Shop now"
                                        icon={<IconRightArrow />}
                                        onClick={() => handleClick(details.website)}
                                    />
                                </StyledItem>
                            ))}
                        <StyledItemAction>
                            <Button
                                color="outline"
                                size="large"
                                label="Show all online stockists"
                                alignSelf="center"
                                onClick={() => handleAll()}
                            />
                        </StyledItemAction>
                    </StyledItems>
                    <StyledClose type="button" onClick={close}>
                        <IconClose />
                    </StyledClose>
                </StyledDialogContent>
            </DialogOverlay>
        </>
    );
};

export default OnlineStockistModal;
